// ═══════════════════════════════════════════════════════════════════════════
// FeedDialogs — the integration feed's modal dialogs (pages/AdminFeed.js).
// ═══════════════════════════════════════════════════════════════════════════
// • CreateConnectionDialog: account, provider, Biz ID, the checklist and the
//   merchant's consent (plan 7.5 steps 2 and 3), then the one-time token and
//   the Atlas steps for UrbanPiper staff (activation shape b, plan 7.4). A
//   reconnect reuses a disconnected connection and starts it over (the .NET
//   host's difference 26, FEED_ALIGN_STATUS.md section 1): the dialog says so.
// • RotateTokenDialog: confirm, then the new one-time token. With
//   allowRevokePrevious (the super admin screen only; the host answers anyone
//   else 403) it offers to revoke the token in use at once, for a token that
//   leaked (difference 27).
// • ActionDialog: confirm pause (with an optional reason), resume, approve or
//   revoke; the caller passes the API call as `run`.
//
// The one-time token lives ONLY in the state of the dialog that called
// createConnection or rotateToken. It is rendered in read-only inputs, never
// in a title, aria-label, data attribute, URL, log or storage; the parent gets
// the connection back without it, and it is gone when the dialog closes.
// Mutations fire only from click handlers, never from effects: React
// StrictMode runs effects twice in development, which would make two
// connections or two tokens.
//
// FeedModal mirrors the look of components/ui.js ConfirmDialog. ConfirmDialog
// itself isn't used here: it logs a failed action to the console, shows
// err.message (for a FeedApiError, the raw snake_case code), has two
// untranslated labels and binds Enter on window.
// ═══════════════════════════════════════════════════════════════════════════
import { useId, useRef, useState } from "react";
import { useT } from "../lib/i18n";
import { createConnection, rotateToken } from "../lib/feedApi";
import { isUuid, isBizId, isTermsVersion, providerName } from "../lib/feedUi";
import {
  inputStyle, selectStyleAdmin, btnSecondary, disabledLook, labelStyle, hintStyle,
  fieldErrorStyle, codeStyle, Banner, FeedErrorText,
} from "./feedKit";

const cancelBtn = {
  padding:"9px 16px", borderRadius:8, border:"1px solid #e2e8f0", background:"#fff", color:"#475569",
  fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"inherit", letterSpacing:"-.1px",
};
const confirmBtn = (danger) => ({
  padding:"9px 16px", borderRadius:8, border:"none", background: danger ? "#dc2626" : "#1e1b4b", color:"#fff",
  fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"inherit", letterSpacing:"-.1px",
  boxShadow: danger ? "0 2px 6px rgba(220,38,38,.25)" : "0 2px 6px rgba(15,23,42,.18)",
});
const bodyText = { fontSize:13.5, color:"#475569", lineHeight:1.55, margin:"0 0 12px", fontWeight:500 };

// ── The modal shell (mirrors ConfirmDialog's overlay and card) ──────────────
export function FeedModal({ title, width = 460, onDismiss, footer, children }) {
  const titleId = useId();
  return (
    <div onClick={onDismiss || undefined}
      style={{ position:"fixed", inset:0, background:"rgba(15,23,42,.5)", backdropFilter:"blur(4px)", WebkitBackdropFilter:"blur(4px)",
        zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} onClick={(e) => e.stopPropagation()}
        style={{ background:"#fff", borderRadius:14, maxWidth:width, width:"100%", maxHeight:"calc(100vh - 32px)", display:"flex",
          flexDirection:"column", boxShadow:"0 24px 60px rgba(15,23,42,.25)", border:"1px solid #e2e8f0", overflow:"hidden" }}>
        <div style={{ padding:"24px 26px 16px", overflowY:"auto" }}>
          <h3 id={titleId} style={{ fontSize:17, fontWeight:700, color:"#0f172a", letterSpacing:"-.3px", margin:"0 0 10px", lineHeight:1.3 }}>
            {title}
          </h3>
          {children}
        </div>
        <div style={{ padding:"14px 26px 20px", display:"flex", gap:10, justifyContent:"flex-end", flexWrap:"wrap", borderTop:"1px solid #f1f5f9" }}>
          {footer}
        </div>
      </div>
    </div>
  );
}

// ── A read-only value with a Copy button ────────────────────────────────────
// navigator.clipboard is missing on plain http and in jsdom; the fallback
// selects the text so Ctrl+C works.
function CopyField({ label, value }) {
  const { t } = useT();
  const id = useId();
  const ref = useRef(null);
  const [copied, setCopied] = useState(null);   // null | "ok" | "fallback"
  async function copy() {
    let ok = false;
    try {
      const clip = typeof navigator !== "undefined" ? navigator.clipboard : null;
      if (clip && typeof clip.writeText === "function") {
        await clip.writeText(value);
        ok = true;
      }
    } catch (_) {
      ok = false;
    }
    if (!ok && ref.current) {
      try {
        ref.current.focus();
        ref.current.select();
      } catch (_) { /* nothing more to do */ }
    }
    setCopied(ok ? "ok" : "fallback");
  }
  return (
    <div>
      <label htmlFor={id} style={labelStyle}>{label}</label>
      <div style={{ display:"flex", gap:6 }}>
        <input id={id} ref={ref} readOnly value={value} spellCheck={false} autoComplete="off"
          onFocus={(e) => e.target.select()}
          style={{ ...inputStyle, flex:1, minWidth:0, fontFamily:"ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", fontSize:12.5 }} />
        <button type="button" onClick={copy} disabled={!value} style={disabledLook(btnSecondary, !value)}>
          {copied === "ok" ? t("feed.token.copied") : t("feed.token.copy")}
        </button>
      </div>
      {copied === "fallback" && <div style={hintStyle}>{t("feed.token.copyFallback")}</div>}
    </div>
  );
}

// ── The one-time token, the webhook URL and header name as the server sent
//    them, and what to do with them ─────────────────────────────────────────
function TokenReveal({ issued, mode }) {
  const { t } = useT();
  const header = issued.headerName || "";
  return (
    <div>
      <Banner kind="warn">{t("feed.token.warning")}</Banner>
      <div style={{ display:"grid", gap:12, marginTop:14 }}>
        <CopyField label={t("feed.token.webhookUrl")} value={issued.webhookUrl || ""} />
        <CopyField label={t("feed.token.headerName")} value={header} />
        <CopyField label={t("feed.token.token")} value={issued.token} />
      </div>
      {mode === "create" ? (
        <div style={{ marginTop:16 }}>
          <div style={{ ...labelStyle, marginBottom:6 }}>{t("feed.token.stepsTitle")}</div>
          <ol style={{ margin:0, paddingInlineStart:20, fontSize:13, color:"#334155", lineHeight:1.6 }}>
            <li>{t("feed.token.step1")}</li>
            <li>{t("feed.token.step2")}</li>
            <li>{t("feed.token.step3")}</li>
            <li>{t("feed.token.step4")}</li>
            <li>{t("feed.token.step5Prefix")}<code style={codeStyle}>{header}</code>{t("feed.token.step5Suffix")}</li>
          </ol>
        </div>
      ) : (
        <p style={{ ...bodyText, marginTop:16 }}>
          {t("feed.rotate.stepPrefix")}<code style={codeStyle}>{header}</code>{t("feed.rotate.stepSuffix")}
        </p>
      )}
      <p style={{ ...hintStyle, marginTop:12 }}>{t("feed.token.share")}</p>
      {mode === "create" && <p style={{ ...hintStyle, marginTop:6 }}>{t("feed.token.next")}</p>}
    </div>
  );
}

// ── Create a connection ─────────────────────────────────────────────────────
// providers: the provider codes the server reports as switched on.
// prefill:   { id, name } handed over from the Accounts list, or null.
// onDone(connection) is called without the token.
export function CreateConnectionDialog({ providers = [], prefill = null, onCancel, onDone }) {
  const { t } = useT();
  const ids = { account: useId(), provider: useId(), biz: useId(), terms: useId() };
  const [accountId, setAccountId] = useState(prefill && prefill.id ? prefill.id : "");
  const [provider, setProvider] = useState(providers[0] || "");
  const [bizId, setBizId] = useState("");
  const [posOk, setPosOk] = useState(false);
  const [consent, setConsent] = useState(false);
  const [terms, setTerms] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [issued, setIssued] = useState(null);   // { connection, token, headerName, webhookUrl }: this dialog only

  const accountOk = isUuid(accountId);
  const bizOk = isBizId(bizId);
  const termsOk = isTermsVersion(terms);
  const providerOk = providers.includes(provider);
  const valid = accountOk && bizOk && termsOk && providerOk && posOk && consent;
  const prefillName = prefill && prefill.id && accountId.trim().toLowerCase() === prefill.id.toLowerCase() ? prefill.name : null;

  async function submit() {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await createConnection({
        accountId: accountId.trim(),
        provider,
        providerAccountRef: bizId.trim(),   // a string: leading zeros stay
        consentTermsVersion: terms.trim(),
      });
      setIssued(res);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  function done() {
    const connection = issued ? issued.connection : null;
    setIssued(null);
    onDone(connection);
  }

  if (issued) {
    return (
      <FeedModal title={t("feed.create.shownTitle")} width={600}
        footer={<button type="button" onClick={done} style={confirmBtn(false)}>{t("feed.token.done")}</button>}>
        <TokenReveal issued={issued} mode="create" />
      </FeedModal>
    );
  }

  return (
    <FeedModal title={t("feed.create.title")} width={600} onDismiss={busy ? undefined : onCancel}
      footer={(
        <>
          <button type="button" onClick={onCancel} disabled={busy} style={disabledLook(cancelBtn, busy)}>{t("feed.cancel")}</button>
          <button type="button" onClick={submit} disabled={!valid || busy} style={disabledLook(confirmBtn(false), !valid || busy)}>
            {busy ? t("feed.working") : t("feed.create.submit")}
          </button>
        </>
      )}>
      <p style={bodyText}>{t("feed.create.intro")}</p>
      <div style={{ display:"grid", gap:14 }}>
        <div>
          <label htmlFor={ids.account} style={labelStyle}>{t("feed.create.account")}</label>
          {prefillName && <div style={{ ...hintStyle, marginTop:0, marginBottom:5 }}>{t("feed.create.accountFrom")}<b>{prefillName}</b></div>}
          <input id={ids.account} value={accountId} onChange={(e) => setAccountId(e.target.value)} disabled={busy}
            spellCheck={false} autoComplete="off" style={{ ...inputStyle, width:"100%", fontFamily:"ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", fontSize:12.5 }} />
          {accountId.trim() && !accountOk
            ? <div style={fieldErrorStyle}>{t("feed.create.accountInvalid")}</div>
            : <div style={hintStyle}>{t("feed.create.accountHint")}</div>}
        </div>
        <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
          <div style={{ flex:"1 1 160px" }}>
            <label htmlFor={ids.provider} style={labelStyle}>{t("feed.create.provider")}</label>
            <select id={ids.provider} value={provider} onChange={(e) => setProvider(e.target.value)} disabled={busy}
              style={{ ...selectStyleAdmin, width:"100%" }}>
              {providers.map((p) => <option key={p} value={p}>{providerName(p)}</option>)}
            </select>
          </div>
          <div style={{ flex:"2 1 220px" }}>
            <label htmlFor={ids.biz} style={labelStyle}>{t("feed.create.bizId")}</label>
            <input id={ids.biz} value={bizId} onChange={(e) => setBizId(e.target.value)} disabled={busy}
              inputMode="numeric" spellCheck={false} autoComplete="off" style={{ ...inputStyle, width:"100%" }} />
            {bizId.trim() && !bizOk
              ? <div style={fieldErrorStyle}>{t("feed.create.bizIdInvalid")}</div>
              : <div style={hintStyle}>{t("feed.create.bizIdHint")}</div>}
          </div>
        </div>
        <div style={{ ...hintStyle, marginTop:-6 }}>{t("feed.create.reconnectHint")}</div>

        <fieldset style={{ border:"1px solid #e2e8f0", borderRadius:8, padding:"10px 14px", margin:0 }}>
          <legend style={{ ...labelStyle, padding:"0 4px", marginBottom:0 }}>{t("feed.create.checklistTitle")}</legend>
          <label style={{ display:"flex", gap:9, alignItems:"flex-start", fontSize:13, color:"#334155", lineHeight:1.5, cursor:"pointer" }}>
            <input type="checkbox" checked={posOk} onChange={(e) => setPosOk(e.target.checked)} disabled={busy} style={{ marginTop:3 }} />
            <span>{t("feed.create.checkPos")}</span>
          </label>
        </fieldset>

        <fieldset style={{ border:"1px solid #e2e8f0", borderRadius:8, padding:"10px 14px", margin:0 }}>
          <legend style={{ ...labelStyle, padding:"0 4px", marginBottom:0 }}>{t("feed.create.consentTitle")}</legend>
          <p style={{ fontSize:12.5, color:"#475569", lineHeight:1.55, margin:"0 0 10px" }}>{t("feed.create.consentBody")}</p>
          <label style={{ display:"flex", gap:9, alignItems:"flex-start", fontSize:13, color:"#334155", lineHeight:1.5, cursor:"pointer" }}>
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} disabled={busy} style={{ marginTop:3 }} />
            <span>{t("feed.create.consentCheck")}</span>
          </label>
          <div style={{ marginTop:10 }}>
            <label htmlFor={ids.terms} style={labelStyle}>{t("feed.create.terms")}</label>
            <input id={ids.terms} value={terms} onChange={(e) => setTerms(e.target.value)} disabled={busy} maxLength={40}
              spellCheck={false} autoComplete="off" style={{ ...inputStyle, width:"100%" }} />
            {terms && !termsOk
              ? <div style={fieldErrorStyle}>{t("feed.create.termsInvalid")}</div>
              : <div style={hintStyle}>{t("feed.create.termsHint")}</div>}
          </div>
        </fieldset>

        {error && <Banner kind="error"><FeedErrorText error={error} /></Banner>}
      </div>
    </FeedModal>
  );
}

// ── Rotate a connection's token ─────────────────────────────────────────────
// allowRevokePrevious: offer "revoke the token in use now" (super admin only).
// Unticked, the request is exactly the contract's (no body); ticked, it asks
// the server to revoke every live token at once.
// onDone() is called once the admin has seen the new token.
export function RotateTokenDialog({ connection, allowRevokePrevious = false, onCancel, onDone }) {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [revokeNow, setRevokeNow] = useState(false);
  const [issued, setIssued] = useState(null);   // { token, headerName, webhookUrl, credential }: this dialog only
  const revoking = allowRevokePrevious && revokeNow;

  async function rotate() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      setIssued(await (revoking ? rotateToken(connection.id, { revokePrevious: true }) : rotateToken(connection.id)));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  function done() {
    setIssued(null);
    onDone();
  }

  if (issued) {
    return (
      <FeedModal title={t("feed.rotate.shownTitle")} width={600}
        footer={<button type="button" onClick={done} style={confirmBtn(false)}>{t("feed.token.done")}</button>}>
        <TokenReveal issued={issued} mode="rotate" />
      </FeedModal>
    );
  }
  return (
    <FeedModal title={t("feed.rotate.title")} onDismiss={busy ? undefined : onCancel}
      footer={(
        <>
          <button type="button" onClick={onCancel} disabled={busy} style={disabledLook(cancelBtn, busy)}>{t("feed.cancel")}</button>
          <button type="button" onClick={rotate} disabled={busy} style={disabledLook(confirmBtn(revoking), busy)}>
            {busy ? t("feed.working") : t("feed.rotate.confirm")}
          </button>
        </>
      )}>
      <p style={bodyText}>
        <b>{connection.accountName || "—"}</b> · {providerName(connection.provider)} · {connection.providerAccountRef}
      </p>
      <p style={bodyText}>{t("feed.rotate.body")}</p>
      {allowRevokePrevious && (
        <div style={{ marginBottom:12 }}>
          <label style={{ display:"flex", gap:9, alignItems:"flex-start", fontSize:13, color:"#334155", lineHeight:1.5, cursor: busy ? "default" : "pointer" }}>
            <input type="checkbox" checked={revokeNow} onChange={(e) => setRevokeNow(e.target.checked)} disabled={busy} style={{ marginTop:3 }} />
            <span>{t("feed.rotate.revokeLabel")}</span>
          </label>
          <div style={{ ...hintStyle, color: revokeNow ? "#991b1b" : hintStyle.color }}>{t("feed.rotate.revokeHint")}</div>
        </div>
      )}
      {error && <Banner kind="error"><FeedErrorText error={error} /></Banner>}
    </FeedModal>
  );
}

// ── Confirm an action; run(options) performs it ─────────────────────────────
// withReason adds the optional pause reason (≤ 200 characters, contract 5.12)
// and passes { reason } to run. The dialog closes itself on success.
export function ActionDialog({ title, body, confirmLabel, danger = false, withReason = false, subject, run, onClose }) {
  const { t } = useT();
  const reasonId = useId();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function confirm() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await run(withReason ? { reason: reason.trim() } : {});
      onClose();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  }

  return (
    <FeedModal title={title} onDismiss={busy ? undefined : onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} disabled={busy} style={disabledLook(cancelBtn, busy)}>{t("feed.cancel")}</button>
          <button type="button" onClick={confirm} disabled={busy} style={disabledLook(confirmBtn(danger), busy)}>
            {busy ? t("feed.working") : confirmLabel}
          </button>
        </>
      )}>
      {subject && <p style={bodyText}>{subject}</p>}
      <p style={bodyText}>{body}</p>
      {withReason && (
        <div style={{ marginBottom:12 }}>
          <label htmlFor={reasonId} style={labelStyle}>{t("feed.pause.reason")}</label>
          <input id={reasonId} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} disabled={busy}
            autoComplete="off" style={{ ...inputStyle, width:"100%" }} />
        </div>
      )}
      {error && <Banner kind="error"><FeedErrorText error={error} /></Banner>}
    </FeedModal>
  );
}
